import { useEffect, useRef, useState,  forwardRef } from "react"
import { BillDetail } from "./BillDetail"
import QRCode from "qrcode"
// import logo from "./../../../../../public/img/logo.png"
const WALLETS = ["GoPay", "OVO", "Dana", "LinkAja", "Semua Bank"]

function countByPercentage (number, percentage) {
  // console.log(number, percentage)
  return Math.ceil(((Number(percentage) / 100) * Number(number)))
}

const useCountdown = (isoTarget) => {
  const calc = () => {
    if (!isoTarget) return null
    const diff = Math.floor((new Date(isoTarget) - Date.now()) / 1000)
    return diff > 0 ? diff : 0
  }
  const [secs, setSecs] = useState(calc)

  useEffect(() => {
    if (!isoTarget) return
    setSecs(calc())
    const id = setInterval(() => {
      setSecs(prev => {
        if (prev <= 1) { clearInterval(id); return 0 }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(id)
  }, [isoTarget])

  return secs
}

const QrCanvas = forwardRef(({ content, logo }, ref) => {
  const internalRef = useRef(null)
  const canvasRef = ref || internalRef

  useEffect(() => {
    if (!content || !canvasRef.current) return

    // 1. generate QR onto offscreen canvas first
    const offscreen = document.createElement("canvas")

    QRCode.toCanvas(offscreen, content, {
      width: 320,
      margin: 2,
      errorCorrectionLevel: "H",
      color: { dark: "#1a1a2e", light: "#ffffff" },
    }, () => {
      const size = offscreen.width

      // set visible canvas to same size
      const canvas = canvasRef.current
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext("2d")

      // draw QR onto visible canvas
      ctx.drawImage(offscreen, 0, 0)

      // draw logo on top if provided
      if (!logo) return

      const img = new Image()
      img.src = logo
      img.onload = () => {
        const maxLogoSize = size * 0.18

        // keep natural aspect ratio
        const aspectRatio = img.naturalWidth / img.naturalHeight
        let logoW, logoH

        if (aspectRatio >= 1) {
          // wider than tall
          logoW = maxLogoSize
          logoH = maxLogoSize / aspectRatio
        } else {
          // taller than wide
          logoH = maxLogoSize
          logoW = maxLogoSize * aspectRatio
        }

        const x = (size - logoW) / 2
        const y = (size - logoH) / 2
        const padding = 6

        // bg sized to actual logo dimensions
        const bgW = logoW + padding * 2
        const bgH = logoH + padding * 2
        const bgX = x - padding
        const bgY = y - padding
        const bgR = 10

        // white rounded rect bg
        ctx.save()
        ctx.beginPath()
        ctx.moveTo(bgX + bgR, bgY)
        ctx.lineTo(bgX + bgW - bgR, bgY)
        ctx.quadraticCurveTo(bgX + bgW, bgY, bgX + bgW, bgY + bgR)
        ctx.lineTo(bgX + bgW, bgY + bgH - bgR)
        ctx.quadraticCurveTo(bgX + bgW, bgY + bgH, bgX + bgW - bgR, bgY + bgH)
        ctx.lineTo(bgX + bgR, bgY + bgH)
        ctx.quadraticCurveTo(bgX, bgY + bgH, bgX, bgY + bgH - bgR)
        ctx.lineTo(bgX, bgY + bgR)
        ctx.quadraticCurveTo(bgX, bgY, bgX + bgR, bgY)
        ctx.closePath()
        ctx.fillStyle = "#ffffff"
        ctx.fill()
        ctx.restore()

        // clip to logo shape (rounded rect matching logo dimensions)
        const clipR = 8
        ctx.save()
        ctx.beginPath()
        ctx.moveTo(x + clipR, y)
        ctx.lineTo(x + logoW - clipR, y)
        ctx.quadraticCurveTo(x + logoW, y, x + logoW, y + clipR)
        ctx.lineTo(x + logoW, y + logoH - clipR)
        ctx.quadraticCurveTo(x + logoW, y + logoH, x + logoW - clipR, y + logoH)
        ctx.lineTo(x + clipR, y + logoH)
        ctx.quadraticCurveTo(x, y + logoH, x, y + logoH - clipR)
        ctx.lineTo(x, y + clipR)
        ctx.quadraticCurveTo(x, y, x + clipR, y)
        ctx.closePath()
        ctx.clip()
        ctx.drawImage(img, x, y, logoW, logoH)  // use logoW/logoH not logoSize
        ctx.restore()
      }
    })
  }, [content, logo])

  return (
    <div style={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      background: "linear-gradient(135deg, #667eea22 0%, #764ba222 100%)",
      borderRadius: 10,
      padding: "10px 10px 8px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.10), 0 1.5px 6px rgba(0,0,0,0.07)",
      border: "1px solid rgba(255, 255, 255, 0.6)",
      gap: 10,
    }}>
      <div style={{
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(0,0,0,0.10)",
        lineHeight: 0,
      }}>
        <canvas
          ref={canvasRef}
          style={{ display: "block", maxWidth: "100%" }}
        />
      </div>
    </div>
  )
})

const CountdownBadge = ({ secs }) => {
  if (secs === null) return null

  const expired = secs === 0
  const mm = String(Math.floor(secs / 60)).padStart(2, "0")
  const ss = String(secs % 60).padStart(2, "0")

  return (
    <div className={`d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill border ${expired ? "border-danger bg-danger-subtle text-danger" : secs < 60 ? "border-warning bg-warning-subtle text-warning-emphasis" : "border-secondary-subtle bg-body-secondary text-muted"}`}
      style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}
    >
      <i className={`fa ${expired ? "fa-times-circle" : "fa-clock-o"}`} aria-hidden="true" />
      {expired ? "QR Kedaluwarsa" : `QR Hangus Dalam ${mm}:${ss}`}
    </div>
  )
}

export const QrisPanel = ({ currPeriod, discount, total, qrisState, onGenerate, onRefresh, profile = {} }) => {
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  const { status, qrContent, validityPeriod, referenceNo, error } = qrisState
  const secs = useCountdown(validityPeriod)
  const expired = secs === 0 && validityPeriod
  const {
    qrisAdminFeePercentages,
    qrisAdminFeeAmount,
    qrisIsPercentages
  } = profile

  const isPercentages = qrisIsPercentages ?? false
  const fee = Number(qrisAdminFeePercentages ?? 0)

  const finalFee = !isPercentages
    ? Number(qrisAdminFeeAmount ?? 0)
    : countByPercentage(Number(total), Number(fee))

    const handleCopyImage = () => {
      if (!canvasRef.current) return;
      canvasRef.current.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // showToast("Copy not supported in this browser", "warning");
        }
      });
    };

    const handleDownload = () => {
      if (!canvasRef.current) return;
      const link = document.createElement("a");
      link.download = `qrisgoodnet-${referenceNo || +new Date()}.png`;
      link.href = canvasRef.current.toDataURL("image/png");
      link.click();
      // showToast("QR image downloaded!");
    };

  return (
    <div className="px-3 pb-3">
      <hr className="mt-0 mb-3" style={{ opacity: 0.15 }} />
      <BillDetail
        currPeriod={currPeriod}
        fees={finalFee}
        discount={discount}
        total={total}
      />

      <div className="border-top mt-3 pt-3 text-center">
        <p className="text-uppercase text-muted mb-2" style={{ fontSize: 11, letterSpacing: "0.05em", fontWeight: 600 }}>
          Scan untuk bayar
        </p>

        {/* idle */}
        {status === "idle" && (
          <>
            <div className="d-flex flex-wrap justify-content-center gap-1 mb-3">
              {WALLETS.map(w => (
                <span key={w} className="badge rounded-pill bg-body-secondary text-secondary border" style={{ fontSize: 11 }}>
                  {w}
                </span>
              ))}
            </div>
            <button className="btn btn-sm btn-outline-secondary px-4" onClick={onGenerate}>
              <i className="fa fa-qrcode me-1" aria-hidden="true" />
              Bayar Dengan QRIS
            </button>
          </>
        )}

        {/* loading */}
        {status === "loading" && (
          <>
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
              <span className="visually-hidden">Memuat...</span>
            </div>
            <p className="text-muted small mb-0">Membuat QR Code...</p>
          </>
        )}

        {/* error */}
        {status === "error" && (
          <>
            <i className="fa fa-exclamation-circle text-danger mb-2" style={{ fontSize: 28 }} aria-hidden="true" />
            <p className="text-danger small mb-2">{error}</p>
            <button className="btn btn-sm btn-danger" onClick={onGenerate}>Coba Lagi</button>
          </>
        )}

        {/* done */}
        {status === "done" && qrContent && (
          <div className="d-flex flex-column align-items-center gap-2">
            <div style={{ position: "relative", display: "inline-block" }}>
              <QrCanvas
                // logo={logo}
                content={qrContent}
                ref={canvasRef}
              />
              {expired && (
                <div
                  className="d-flex flex-column align-items-center justify-content-center rounded bg-white bg-opacity-90"
                  style={{ position: "absolute", inset: 0 }}
                >
                  <i className="fa fa-info text-danger mb-1" style={{ fontSize: 28 }} aria-hidden="true" />
                  <span className="text-danger fw-semibold" style={{ fontSize: 13 }}>QR Kedaluwarsa</span>
                </div>
              )}
            </div>

            <CountdownBadge secs={secs} />

            <p className="text-muted mb-1" style={{ fontSize: 11 }}>
              Scan melalui GoPay, OVO, Dana, LinkAja, atau mobile banking
            </p>
          {!expired
          ? (<></>)
          : <button className="btn btn-sm btn-outline-secondary" onClick={onRefresh}>
              <i className="fa fa-refresh me-1" aria-hidden="true" />
              Perbarui QR
            </button>
          }
          </div>
        )}
      </div>
        {!expired && (status === "done" && qrContent)
          ? (<>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-secondary flex-fill"
                onClick={handleCopyImage}
              >
                {copied ? <i className="fa fa-check"/> : <i className="fa fa-clipboard"/>}
                {copied ? " Copied" : " Copy"}
              </button>
              <button
                className="btn btn-sm btn-outline-primary flex-fill"
                onClick={handleDownload}
              >
                <i className="fa fa-download"/> Download
              </button>
            </div>
          </>)
        : <></>
      }
    </div>
  )
}